import React from "react"
import {useTranslation} from "react-i18next"

// Statements
import {StatementMatch} from "../ecgStatements"

// Styles
import useStyles from "./styles"

type StatementSuggestionsProps = {
    matches: StatementMatch[]
    activeIndex: number
    /** Id of the listbox, so the textarea can point `aria-controls` at it. */
    id: string
    onPick(match: StatementMatch): void
    onHover(index: number): void
}

/**
 * The list that drops under the findings box while a statement is being typed.
 *
 * Deliberately not a portal and not positioned at the caret: it is docked to
 * the bottom of the textarea, which needs no caret-coordinate arithmetic and
 * cannot drift when the field scrolls or grows. The cost is that it always
 * appears in the same place; for a list of at most eight lines that reads as
 * steadiness rather than a limitation.
 *
 * `onMouseDown` is where the pick happens, NOT `onClick`: the textarea's blur
 * fires before a click completes and would close this list first, so a click
 * would land on nothing. Preventing the default on mousedown also keeps focus
 * in the textarea, so typing can continue straight after.
 *
 * @param props
 */
export default function StatementSuggestions(props: StatementSuggestionsProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {matches, activeIndex, id, onPick, onHover} = props;

    if (matches.length === 0) {
        return null
    }

    return (
        <ul className={styles.list} id={id} role="listbox">
            {matches.map((match, index) => (
                <li
                    key={match.statement.key}
                    id={`${id}-option-${index}`}
                    role="option"
                    aria-selected={index === activeIndex}
                    className={`${styles.item} ${index === activeIndex ? styles.item__active : ""}`}
                    onMouseDown={event => {
                        event.preventDefault();
                        onPick(match)
                    }}
                    onMouseEnter={() => onHover(index)}
                >
                    <span className={styles.text}>{match.label}</span>
                    {/*
                      * Sits immediately after the statement, where the leads
                      * themselves will go, rather than pushed to the far edge
                      * of the row — and styled as a pill so it reads as
                      * something that happens when you choose this line. The
                      * whole row is the click target, so the pill is live
                      * without needing a handler of its own.
                      */}
                    {match.statement.leads && (
                        <span className={styles.leads}>{t("statements.leads")}</span>
                    )}
                </li>
            ))}
        </ul>
    )
}
